import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import type { Category } from "@aurelia/types";
import { useParallax } from "../motion/RevealOnScroll";

const FALLBACK_GRADIENTS = [
  "linear-gradient(135deg, var(--color-accent-soft), transparent)",
  "linear-gradient(135deg, var(--color-surface-raised), var(--color-accent-soft))",
  "linear-gradient(160deg, var(--color-accent-soft), var(--color-surface))"
];

export function CategoryTile({ category, index }: { category: Category; index: number }) {
  const bgRef = useRef<HTMLDivElement>(null);
  useParallax(bgRef, 0.15);

  return (
    <Link
      to={`/category/${category.slug}`}
      className="group relative aspect-square rounded-2xl overflow-hidden bg-surface border border-border flex items-end p-5 transition-transform duration-150 ease-physical active:scale-[0.98]"
    >
      {/* Scale lives on this wrapper, not bgRef itself — bgRef's transform
          is owned by the GSAP parallax tween, and a CSS hover-scale class
          on the same element would fight it for the transform property. */}
      <div className="absolute inset-0 transition-transform duration-500 ease-physical group-hover:scale-110">
        <div
          ref={bgRef}
          aria-hidden
          className="absolute inset-0 -top-1/4 h-[150%]"
          style={{
            backgroundImage: category.imageUrl ? `url(${category.imageUrl})` : FALLBACK_GRADIENTS[index % FALLBACK_GRADIENTS.length],
            backgroundSize: "cover",
            backgroundPosition: "center"
          }}
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
      <span className="relative z-10 flex items-center gap-1.5 text-lg font-medium tracking-tight text-white transition-transform duration-300 ease-physical group-hover:-translate-y-1">
        {category.name}
        <ArrowRight
          size={16}
          className="opacity-0 -translate-x-1 transition-all duration-300 ease-physical group-hover:opacity-100 group-hover:translate-x-0"
          aria-hidden
        />
      </span>
    </Link>
  );
}
